import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { contentAPI } from "../lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await contentAPI.subscribe(email);
      toast.success(
        res.status === "already_subscribed"
          ? "You are already on our list."
          : "Subscribed. Check your inbox for our next OBBBA briefing."
      );
      setEmail("");
    } catch (e) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      className="bg-foreground text-background border-t-2 border-foreground mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-background text-foreground flex items-center justify-center font-heading font-bold text-2xl">
                T
              </div>
              <span className="font-heading text-2xl font-bold">TaxShift 2026</span>
            </div>
            <p className="font-body text-sm text-background/70 max-w-md leading-relaxed mb-6">
              An independent editorial calculator suite explaining how the One Big Beautiful Bill
              Act (OBBBA, Public Law 119-21) reshaped federal taxation for 2026 and beyond.
            </p>
            <form onSubmit={subscribe} className="flex gap-0" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-background text-foreground px-4 py-3 font-mono text-sm border-0 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-primary text-primary-foreground font-mono uppercase tracking-widest text-xs px-5 py-3 hover:brightness-110 transition disabled:opacity-50"
                data-testid="footer-newsletter-submit"
              >
                {loading ? "…" : "Subscribe"}
              </button>
            </form>
            <p className="font-mono text-[10px] uppercase tracking-widest text-background/50 mt-3">
              Weekly editorial · No spam · Unsubscribe anytime
            </p>

            <div className="mt-6 flex items-center gap-4" data-testid="footer-socials">
              <span className="font-mono text-[10px] uppercase tracking-widest text-background/50">Follow</span>
              <a
                href="https://www.facebook.com/TaxShift2026"
                target="_blank"
                rel="noopener noreferrer me"
                aria-label="TaxShift on Facebook"
                className="text-background/70 hover:text-primary transition-colors"
                data-testid="social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82V14.706h-3.13v-3.624h3.13V8.413c0-3.1 1.894-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.31h3.587l-.467 3.624h-3.12V24h6.116C23.407 24 24 23.407 24 22.676V1.325C24 .593 23.407 0 22.675 0z"/>
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-mono uppercase tracking-widest text-xs text-background/60 mb-4">
              Calculators
            </h4>
            <ul className="space-y-2.5 font-body text-sm">
              <li><Link to="/calculator/tax-bracket-2026" className="hover:text-primary" data-testid="footer-link-bracket">Tax Bracket</Link></li>
              <li><Link to="/calculator/standard-deduction-2026" className="hover:text-primary" data-testid="footer-link-std">Standard Deduction</Link></li>
              <li><Link to="/calculator/child-tax-credit-2026" className="hover:text-primary" data-testid="footer-link-ctc">Child Tax Credit</Link></li>
              <li><Link to="/calculator/capital-gains-2026" className="hover:text-primary" data-testid="footer-link-ltcg">Capital Gains</Link></li>
              <li><Link to="/calculator/salt-deduction-2026" className="hover:text-primary" data-testid="footer-link-salt">SALT Deduction</Link></li>
              <li><Link to="/calculator/qbi-deduction-2026" className="hover:text-primary" data-testid="footer-link-qbi">QBI Deduction</Link></li>
              <li><Link to="/calculator/estate-tax-2026" className="hover:text-primary" data-testid="footer-link-estate">Estate Tax</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono uppercase tracking-widest text-xs text-background/60 mb-4">
              Company
            </h4>
            <ul className="space-y-2.5 font-body text-sm">
              <li><Link to="/about" className="hover:text-primary" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/blog" className="hover:text-primary" data-testid="footer-link-blog">Editorial</Link></li>
              <li><Link to="/editorial" className="hover:text-primary" data-testid="footer-link-editorial-standards">Editorial Standards</Link></li>
              <li><Link to="/contact" className="hover:text-primary" data-testid="footer-link-contact">Contact</Link></li>
              <li><Link to="/disclaimer" className="hover:text-primary" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-primary" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-primary" data-testid="footer-link-terms">Terms of Service</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-primary" data-testid="footer-link-cookies">Cookie Policy</Link></li>
              <li><Link to="/gdpr" className="hover:text-primary" data-testid="footer-link-gdpr">GDPR (EU)</Link></li>
            </ul>
          </div>
        </div>

        {/* Comprehensive sitewide link directory — visible to crawlers + users.
            Helps SEO via internal link density and gives readers a one-stop
            map to every calculator, state page, and OBBBA guide. */}
        <div className="border-t border-background/20 mt-12 pt-10" data-testid="footer-directory">
          <h4 className="font-mono uppercase tracking-widest text-xs text-background/60 mb-6">
            Every calculator · Every state · Every guide
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-xs">
            <div>
              <h5 className="font-heading text-sm font-semibold mb-3 text-background">
                Federal calculators (11)
              </h5>
              <ul className="space-y-1.5 font-body text-background/75 leading-relaxed">
                <li><Link to="/calculator/tax-bracket-2026" className="hover:text-primary">2026 Tax Brackets</Link></li>
                <li><Link to="/calculator/standard-deduction-2026" className="hover:text-primary">Standard Deduction</Link></li>
                <li><Link to="/calculator/child-tax-credit-2026" className="hover:text-primary">$2,200 Child Tax Credit</Link></li>
                <li><Link to="/calculator/capital-gains-2026" className="hover:text-primary">Capital Gains</Link></li>
                <li><Link to="/calculator/salt-deduction-2026" className="hover:text-primary">$40k SALT Cap</Link></li>
                <li><Link to="/calculator/qbi-deduction-2026" className="hover:text-primary">QBI 20% Deduction</Link></li>
                <li><Link to="/calculator/estate-tax-2026" className="hover:text-primary">$15M Estate Tax</Link></li>
                <li><Link to="/calculator/no-tax-on-tips" className="hover:text-primary">No Tax on Tips</Link></li>
                <li><Link to="/calculator/no-tax-on-overtime" className="hover:text-primary">No Tax on Overtime</Link></li>
                <li><Link to="/calculator/senior-deduction-2026" className="hover:text-primary">Senior $6k Deduction</Link></li>
                <li><Link to="/calculator/auto-loan-interest-2026" className="hover:text-primary">Auto Loan Interest</Link></li>
              </ul>
            </div>

            <div className="md:col-span-1">
              <h5 className="font-heading text-sm font-semibold mb-3 text-background">
                <Link to="/states" className="hover:text-primary">All 50 states + DC (51)</Link>
              </h5>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-body text-background/75 leading-relaxed">
                {[
                  ["alabama","AL"],["alaska","AK"],["arizona","AZ"],["arkansas","AR"],
                  ["california","CA"],["colorado","CO"],["connecticut","CT"],["delaware","DE"],
                  ["district-of-columbia","DC"],["florida","FL"],["georgia","GA"],["hawaii","HI"],
                  ["idaho","ID"],["illinois","IL"],["indiana","IN"],["iowa","IA"],
                  ["kansas","KS"],["kentucky","KY"],["louisiana","LA"],["maine","ME"],
                  ["maryland","MD"],["massachusetts","MA"],["michigan","MI"],["minnesota","MN"],
                  ["mississippi","MS"],["missouri","MO"],["montana","MT"],["nebraska","NE"],
                  ["nevada","NV"],["new-hampshire","NH"],["new-jersey","NJ"],["new-mexico","NM"],
                  ["new-york","NY"],["north-carolina","NC"],["north-dakota","ND"],["ohio","OH"],
                  ["oklahoma","OK"],["oregon","OR"],["pennsylvania","PA"],["rhode-island","RI"],
                  ["south-carolina","SC"],["south-dakota","SD"],["tennessee","TN"],["texas","TX"],
                  ["utah","UT"],["vermont","VT"],["virginia","VA"],["washington","WA"],
                  ["west-virginia","WV"],["wisconsin","WI"],["wyoming","WY"],
                ].map(([slug, abbr]) => (
                  <Link
                    key={slug}
                    to={`/calculators/state-income-tax/${slug}`}
                    className="hover:text-primary"
                  >
                    {abbr}
                  </Link>
                ))}
              </div>
            </div>

            <div>
              <h5 className="font-heading text-sm font-semibold mb-3 text-background">
                <Link to="/blog" className="hover:text-primary">OBBBA guides (18+)</Link>
              </h5>
              <ul className="space-y-1.5 font-body text-background/75 leading-relaxed">
                <li><Link to="/blog/obbba-tax-changes-explained" className="hover:text-primary">OBBBA tax changes explained</Link></li>
                <li><Link to="/blog/no-tax-on-tips-complete-guide" className="hover:text-primary">No Tax on Tips guide</Link></li>
                <li><Link to="/blog/no-tax-on-overtime-explained" className="hover:text-primary">No Tax on Overtime explained</Link></li>
                <li><Link to="/blog/senior-6000-deduction-guide" className="hover:text-primary">Senior $6k Deduction guide</Link></li>
                <li><Link to="/blog/40k-salt-cap-phaseout-2026" className="hover:text-primary">$40k SALT cap phase-out</Link></li>
                <li><Link to="/blog/2026-tax-brackets-inflation-adjusted" className="hover:text-primary">2026 tax brackets</Link></li>
                <li><Link to="/blog/auto-loan-interest-deduction-2026" className="hover:text-primary">Auto loan interest deduction</Link></li>
                <li><Link to="/blog/child-tax-credit-2026-obbba" className="hover:text-primary">Child Tax Credit under OBBBA</Link></li>
                <li><Link to="/blog/standard-deduction-2026-amounts" className="hover:text-primary">Standard deduction amounts</Link></li>
                <li><Link to="/blog/estate-tax-exemption-15-million" className="hover:text-primary">$15M estate tax exemption</Link></li>
                <li><Link to="/blog/qbi-deduction-permanent-obbba" className="hover:text-primary">QBI made permanent</Link></li>
                <li><Link to="/blog/bonus-depreciation-100-percent-return" className="hover:text-primary">100% bonus depreciation</Link></li>
                <li><Link to="/blog/section-179-expensing-2-5-million" className="hover:text-primary">Section 179 $2.5M</Link></li>
                <li><Link to="/blog/rd-expensing-restored-obbba" className="hover:text-primary">R&amp;D expensing restored</Link></li>
                <li><Link to="/blog/dependent-care-credit-enhanced-2026" className="hover:text-primary">Enhanced dependent care credit</Link></li>
                <li><Link to="/blog/529-plan-obbba-expansion" className="hover:text-primary">529 plan OBBBA expansion</Link></li>
                <li><Link to="/blog/1099-threshold-2000-2026" className="hover:text-primary">1099 threshold rises to $2,000</Link></li>
                <li><Link to="/blog/tax-planning-strategies-2026" className="hover:text-primary">2026 tax planning strategies</Link></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-background/20 mt-12 pt-8 flex flex-col md:flex-row justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-widest text-background/60">
            © 2026 TaxShift · Not financial advice · Real IRS figures under OBBBA (P.L. 119-21)
          </p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-background/60">
            Built for the OBBBA Era
          </p>
        </div>
      </div>
    </footer>
  );
};
